<template>
  <section>
    <View align="start" justify="space-between" class="header" padding-bottom="md">
      <View direction="column">
        <View gap="sm" align="center">
          <Text variant="h2">{{ props.title }}</Text>
          <Button
            v-if="canSort && imageList.length > 5"
            variant="secondary"
            size="small"
            :loading="imagesStore.isSorting"
            @click="sortImages"
          >
            <Icon name="stars-03" size="extra-small" />
            <span>Sort</span>
          </Button>
        </View>
        <Text variant="body-large" color="tertiary" class="hint">
          Drag to reorder. To set a cover photo drag a photo to the first position
        </Text>
      </View>

      <View v-if="props.isVisibleUploadButton" gap="sm">
        <Button variant="secondary" @click="fileUploader.openFileDialog()">
          <Icon name="plus" />
          <span>Add photos</span>
        </Button>
      </View>
    </View>

    <View
      direction="column"
      align="center"
      justify="space-between"
      gap="lg"
      v-if="!imageList.length && !isUploading"
      class="placeholder"
    >
      <View align="center" justify="center" class="icon-container">
        <Icon name="image-plus" />
      </View>

      <View direction="column" gap="xs">
        <Text variant="h4">Upload from your device or drag and drop here</Text>
        <Text variant="body-medium" color="tertiary" align="center">Maximum 25MB photo</Text>
      </View>

      <Button @click="fileUploader.openFileDialog()">
        <Icon name="plus" />
        <span>Add photos</span>
      </Button>
    </View>

    <div class="images-wrapper">
      <ul ref="parentRef" :class="props.variant">
        <template v-if="imageList.length || isUploading">
          <li v-for="(image, index) in imageList" :key="image.id" class="draggable">
            <div class="image" :style="{ backgroundImage: `url('${image.thumbnailUrl || image.url}')` }"></div>
            <div class="cover-photo-badge" v-if="index === 0">Cover image</div>
            <IconButton
              @click="deleteFile(image.id)"
              class="delete-icon"
              variant="secondary"
              shape="square"
              icon="trash"
            />
          </li>

          <li v-for="item in uploadingList" :key="item.id" class="uploading-placeholder">
            <LoadingIndicator />
          </li>

          <View
            as="li"
            direction="column"
            align="center"
            justify="center"
            gap="sm"
            class="upload-placeholder"
            v-for="item in placeholderList"
            :key="item"
            @click="fileUploader.openFileDialog()"
          >
            <Icon name="image-plus" size="large" />
            <Text variant="label-medium">Add photo</Text>
          </View>
        </template>
      </ul>
    </div>

    <DropAreaOverlay @files-drop="fileUploader.addFilesToQueue" />
  </section>
</template>

<script setup lang="ts">
import { useSortable, moveArrayElement } from '@vueuse/integrations/useSortable';
import { v4 as uuid } from 'uuid';
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';

import { Button, IconButton, Icon, LoadingIndicator, Text, View } from '@tallo/design-system';
import { FileDto, useFileUploader, DropAreaOverlay } from '@tallo/file-manager';

import { usePropertyImagesStore } from '../../stores/property-images.store';

/** What the editor needs of a store: the property images store, or a building's with the same shape */
export interface ImagesEditorStore {
  images: FileDto[];
  isSorting: boolean;
  init(): void;
  $reset(): void;
  addImage(file: File): Promise<unknown>;
  deleteImage(imageId: string): Promise<unknown>;
  setReorderedImages(images: FileDto[]): void;
  sortImages(): Promise<unknown>;
}

const props = withDefaults(
  defineProps<{
    title: string;
    isVisibleUploadButton?: boolean;
    variant?: 'large' | 'minimal';
    /** The photos being edited; a unit's by default */
    store?: ImagesEditorStore;
    /** The AI order is a unit's; a building's photos are ordered by hand only */
    canSort?: boolean;
  }>(),
  {
    isVisibleUploadButton: true,
    variant: 'large',
    store: undefined,
    canSort: true,
  },
);
const imagesStore: ImagesEditorStore = props.store ?? usePropertyImagesStore();
const fileUploader = useFileUploader({
  multiple: true,
  accept: 'image/*',
  uploadFile: (file) => imagesStore.addImage(file),
});
const images = computed(() => imagesStore.images);

const parentRef = ref<HTMLElement | null>(null);
const imageList = ref<FileDto[]>([]);

useSortable(parentRef, imageList, {
  delay: 50,
  delayOnTouchOnly: true,
  draggable: '.draggable',
  ghostClass: 'ghost',
  animation: 100,
  onUpdate(event) {
    moveArrayElement(imageList.value, event.oldIndex!, event.newIndex!);

    nextTick(() => {
      imagesStore.setReorderedImages(imageList.value);
    });
  },
});

const uploadingList = computed(() => fileUploader.uploadFiles.value);
const isUploading = computed(() => uploadingList.value.length > 0);
const placeholderList = computed(() => {
  const uploadMinLimit = 5;
  const imageCount = imageList.value.length + uploadingList.value.length;

  return Array(imageCount >= uploadMinLimit ? 1 : uploadMinLimit - imageCount)
    .fill(null)
    .map(() => `upload-button-${uuid()}`);
});

onMounted(() => imagesStore.init());
onUnmounted(() => imagesStore.$reset());

function deleteFile(fileId: string) {
  imagesStore.deleteImage(fileId);
}

function sortImages() {
  imagesStore.sortImages();
}

watch(images, (value) => (imageList.value = value), { immediate: true });

defineExpose({
  isUploading,
});
</script>

<style lang="scss" scoped>
@import 'styles/mixins/layout.mixins';

.placeholder {
  text-align: center;
  padding: 1rem;
  padding-block: 2.5rem;

  background-color: var(--t-color-neutral-white);
  border: 1px dashed var(--t-color-neutral-600);
  border-radius: var(--t-border-radius-sm);

  .icon-container {
    width: 5rem;
    height: 5rem;

    background-color: var(--t-color-neutral-100);
    border-radius: var(--t-border-radius-half-rounded);
  }
}

.images-wrapper {
  width: 100%;

  ul {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(13rem, 1fr));
    grid-auto-rows: 1fr;
    gap: var(--t-spacing-sm);

    @include mobile {
      grid-template-columns: repeat(2, 1fr);
    }

    li {
      display: flex;
      align-items: center;
      justify-content: center;

      width: 100%;
      list-style: none;
      position: relative;
      padding: 0;
      margin: 0;
      aspect-ratio: 1;
      background-color: var(--t-color-neutral-200);
      border-radius: var(--t-border-radius-sm);
      overflow: hidden;
      user-select: none;
      -webkit-user-select: none;

      &:first-child:has(:not(.upload-placeholder)) {
        grid-column: 1 / span 2;
        aspect-ratio: 4 / 2;
      }

      &.ghost {
        opacity: 0.3;
      }

      &.upload-placeholder {
        background-color: var(--t-color-neutral-white);
        border: 1px dashed var(--t-color-neutral-600);

        &:hover {
          border-style: solid;
          cursor: pointer;
        }
      }

      &:hover {
        .delete-icon {
          opacity: 1;
        }
      }

      .image {
        width: 100%;
        height: 100%;
        background-size: cover;
        background-repeat: no-repeat;
        background-position: center;
      }

      .cover-photo-badge {
        position: absolute;
        left: var(--t-spacing-sm);
        bottom: var(--t-spacing-sm);
        padding: var(--t-spacing-sm) 0.75rem;

        font: var(--t-typography-label-md-font);
        border-radius: var(--t-border-radius-xs);
        background: rgba(255, 255, 255, 0.8);
      }

      .delete-icon {
        position: absolute;
        top: var(--t-spacing-sm);
        right: var(--t-spacing-sm);
        opacity: 0;
        transition: 300ms ease-in-out;

        @media (hover: none) {
          opacity: 1;
        }
      }
    }
  }
}

@include mobile {
  .header {
    flex-direction: column;
    gap: var(--t-spacing-lg);
    align-items: flex-start;
  }
}
</style>
